// Synchronous head injector - guarantees title + key meta tags are in the DOM
// before paint, which means react-snap captures them at build time even when
// react-helmet-async's async DOM mutations race against the snapshot.
//
// This is intentionally minimal: full SEO blocks (Helmet) still run for runtime
// SPA navigation (rich schema, og:image, twitter cards). This util just locks
// down the critical "title + description + canonical + og/twitter title+desc"
// for crawlers that snapshot quickly.

export function applyHead(title, description, canonicalUrl, imageUrl) {
  if (typeof document === 'undefined') return;

  if (title) document.title = title;

  // For og:image/twitter:image we want to *replace* the default generic
  // social-preview.jpg that public/index.html ships with, so social crawlers
  // (Facebook, LinkedIn, X, iMessage, WhatsApp, Slack) snapshot the per-page
  // cover instead of the homepage card. We remove every existing og:image* /
  // twitter:image* tag first so the new value wins regardless of crawler
  // "first vs last tag" heuristics.
  const replaceImageTags = (url) => {
    const selectors = [
      'meta[property="og:image"]',
      'meta[property="og:image:secure_url"]',
      'meta[property="og:image:type"]',
      'meta[property="og:image:width"]',
      'meta[property="og:image:height"]',
      'meta[property="og:image:alt"]',
      'meta[name="twitter:image"]',
      'meta[name="twitter:image:alt"]',
    ];
    selectors.forEach((s) => {
      document.head.querySelectorAll(s).forEach((el) => el.parentNode.removeChild(el));
    });
    const make = (sel, attr, attrName, content) => {
      const el = document.createElement('meta');
      el.setAttribute(attr, attrName);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      return el;
    };
    make('og:image', 'property', 'og:image', url);
    make('og:image:secure_url', 'property', 'og:image:secure_url', url);
    make('twitter:image', 'name', 'twitter:image', url);
  };

  const upsert = (selector, attr, value, createIf) => {
    let el = document.head.querySelector(selector);
    if (!el && createIf) {
      el = document.createElement(createIf.tag);
      Object.entries(createIf.attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
    }
    if (el) el.setAttribute(attr, value);
  };

  if (description) {
    upsert('meta[name="description"]', 'content', description, { tag: 'meta', attrs: { name: 'description' } });
    upsert('meta[property="og:description"]', 'content', description, { tag: 'meta', attrs: { property: 'og:description' } });
    upsert('meta[name="twitter:description"]', 'content', description, { tag: 'meta', attrs: { name: 'twitter:description' } });
  }

  if (title) {
    upsert('meta[property="og:title"]', 'content', title, { tag: 'meta', attrs: { property: 'og:title' } });
    upsert('meta[name="twitter:title"]', 'content', title, { tag: 'meta', attrs: { name: 'twitter:title' } });
  }

  if (canonicalUrl) {
    upsert('link[rel="canonical"]', 'href', canonicalUrl, { tag: 'link', attrs: { rel: 'canonical' } });
    upsert('meta[property="og:url"]', 'content', canonicalUrl, { tag: 'meta', attrs: { property: 'og:url' } });
  }

  if (imageUrl) replaceImageTags(imageUrl);
}
